import _ from "lodash";
import { CollectionAdapter } from "../../main/adapters/CollectionAdapter/CollectionAdapter";
import { HttpAdapter } from "../../main/adapters/HttpAdapter";
import { UserDataAdapter } from "../../presentation/adpters/UserDataAdapter";
import { getDate } from "../../utils/date/date";
import { Company } from "../Company/Company";

const InsightCollection = new Mongo.Collection("Insights", {
    connection: null
});

/**
 * @class
 * Responsavel por cuidar dos dados dos Insights usados no dashboard
 *
 *
 */
export class Insight extends CollectionAdapter {
    HttpAdapter = new HttpAdapter();
    baseUrlAnalyzer = `${publicSettings.aws.baseUrls.dataAnalizer}/insight`;

    constructor({ PopulateManager, Company } = {}) {
        super(InsightCollection);
        this.PopulateManager = PopulateManager;
        this.Company = Company;
    }

    async populateAccountingByDate({ accountingId, source, type, date }) {
        const dateFormatted =
            date.month < 9 ? `0${date.month + 1}` : date.month + 1;
        const sourceWithType = `${source}-${type}-${dateFormatted}`;
        const managerInsightPoints = this.PopulateManager.findOneCollection({
            collections: { $eq: "Insights" }
        });
        const keys = _.get(managerInsightPoints, "keys") || [];
        try {
            if (!keys.includes(sourceWithType)) {
                const response = await this.getMonitoringBySource({
                    accountingId,
                    source,
                    type,
                    date,
                    summary: "accounting"
                });
                if (response.length) {
                    response.forEach((insight) => {
                        this.create({
                            ...insight,
                            accountingId,
                            source,
                            type,
                            scenarioId: "ORIGINAL",
                            period: "monthly"
                        });
                    });
                }
            }
        } catch (error) {
            console.error(error);
        }
    }

    async populateAccountingByLastPointsAvailable({
        accountingId,
        source,
        type
    }) {
        const sourceWithType = `${source}-${type}`;
        const managerInsightPoints = this.PopulateManager.findOneCollection({
            collections: { $eq: "insights" }
        });
        const keys = _.get(managerInsightPoints, "keys") || [];
        try {
            if (!keys.includes(sourceWithType)) {
                const response = await this.HttpAdapter.call(
                    `${this.baseUrlAnalyzer}/get-aggregated`,
                    {
                        accountingId: accountingId,
                        options: {
                            source: source,
                            type: type,
                            latest: true,
                            summary: "accounting"
                        }
                    }
                );
                if (response?.length) {
                    response.forEach((insight) => {
                        this.create({
                            ...insight,
                            accountingId,
                            source,
                            type,
                            scenarioId: "ORIGINAL",
                            period: "monthly"
                        });
                    });
                }

                this.PopulateManager.modify(
                    { collections: { $eq: "insights" } },
                    {
                        $addToSet: {
                            keys: sourceWithType,
                            keysBefore: sourceWithType
                        }
                    }
                );
            }
        } catch (error) {
            console.error(error);
        }
    }

    async populateCompanyMonitoringBySource({
        accountingId,
        companyId,
        source,
        type,
        date,
        summary
    }) {
        const mergeForSelects = [];
        mergeForSelects.push(`${source}.${type}.${date.year}.${date.month}`);
        const managerInishgts =
            this.PopulateManager.findOneCollection({
                collections: { $eq: "insights" }
            }) || {};

        const keys = _.get(managerInishgts, "keys") || [];
        const hasNewYear = _.difference(mergeForSelects, keys);
        if (hasNewYear.length) {
            const yearsSearch = [];
            hasNewYear.forEach((mergeForSelect) => {
                const params = mergeForSelect.split(/\./);
                yearsSearch.push(parseInt(params[2]));
            });
            const insights = await this.getMonitoringBySource({
                accountingId,
                companyId,
                source,
                type,
                date,
                summary
            });
            if (!insights.length) return [];
            for (let insight of insights) {
                this.create({
                    ...insight,
                    companyId: companyId,
                    source: source,
                    type: type,
                    scenarioId: "ORIGINAL",
                    accountingId: accountingId,
                    period: "monthly",
                    date: getDate(insight.date)
                });
            }
            this.PopulateManager.modify(
                { collections: { $eq: "insights" } },
                {
                    $set: {
                        keys: _.uniq([...mergeForSelects, ...keys]),
                        keysBefore: [...keys]
                    }
                }
            );
        }
    }

    /**
     * @public
     * Busca insights por source e companyId
     * @param {Object} param0
     * @returns
     */
    async getMonitoringBySource({
        accountingId,
        companyId,
        source,
        type,
        date,
        seriesIds,
        summary
    }) {
        const input = {
            accountingId: accountingId,
            options: {
                source: source,
                type: type,
                date: {
                    month: String(date.month).padStart(2, "0"),
                    year: String(date.year)
                }
            }
        };
        if (summary) input.options.summary = summary;
        if (companyId) input.companyId = companyId;
        if (seriesIds?.length) input.options.seriesIds = seriesIds;
        try {
            const response = await this.HttpAdapter.call(
                `${this.baseUrlAnalyzer}/get-aggregated`,
                input
            );
            return response;
        } catch (err) {
            return {};
        }
    }

    async getInsightsGroupedByType(data) {
        const insights = { audit: [], health: [], insight: [] };
        const allInsights = await this.getMonitoringBySource({
            accountingId: data.accountingId,
            companyId: data.companyId,
            source: data.source,
            type: data.type,
            date: {
                month: data.date.month,
                year: data.date.year
            },
            seriesIds: data.serieIds
        });
        insights.audit = allInsights.filter((i) => i.type === "audit");
        insights.health = allInsights.filter((i) => i.type === "health");
        insights.insight = allInsights.filter((i) => i.type === "insight");
        return insights;
    }

    async updateOne(selector, modifier) {
        if (!selector || !modifier) return false;
        const response = await this.HttpAdapter.call(
            `${this.baseUrlAnalyzer}/insight-update-one`,
            {
                selector,
                modifier,
                options: {}
            }
        );

        this.modify(selector, modifier);

        return response;
    }

    /**
     * @private
     * Após remover um insight do back end chama remoção dos dados locais
     * @param {Object}
     */
    _afterRemove(selector) {
        this.delete(selector);
    }

    /**
     * @public
     * Responsavel por atribuir feedback a um insight
     *
     * @param {Object} param0
     */
    async feedback({ _id, like, comment, option, user }) {
        const extraAttrs = like ? {} : { option, text: comment };

        const modifier = {
            $push: {
                feedback: {
                    username: user.username,
                    userId: user._id,
                    like,
                    date: new Date(),
                    ...extraAttrs
                }
            }
        };

        try {
            await this.updateOne({ _id: { $eq: _id } }, modifier);

            return true;
        } catch (err) {
            console.error(err);
            return false;
        }
    }

    async populate(selector, options = {}, fields) {
        let response = { list: [] };
        options = {
            ...options,
            limit: "Infinity"
        };
        response = await this.findCloud(selector, {
            ...options,
            compress: true
        });
        if (!_.isEmpty(response.list)) {
            this.deleteMany(selector);
            this.createMany(response.list, fields);
        }
        return true;
    }

    populateByCompanies(insights, accountingId) {
        if (!_.isEmpty(insights)) {
            this.deleteMany({ accountingId: { $eq: accountingId } });
            this.createMany(insights, { accountingId });
        }
    }

    createMany(data = [], fields) {
        const total = data.length;
        if (!total) return false;
        for (let i = 0; i < total; i++) {
            const document = { ...data[i], ...fields };
            document.period = "monthly";
            if (document && _.get(document, "date")) {
                document.date = getDate(document.date);
                this.create(document);
            }
        }
        return true;
    }

    /**
     * @private
     * Garante que não vai buscar insights de scenarios
     * @param {String} scenarioId
     *
     * @returns {Boolean}
     */
    _hasScenario(scenarioId) {
        return scenarioId && scenarioId != "ORIGINAL";
    }

    /**
     * @private
     *
     * @param {Array} dataInsights
     * @param {Array} seriesNames
     *
     * @return
     */
    _filter(dataInsights, seriesNames) {
        const _normalize = (value) => _.deburr(value).toUpperCase();

        seriesNames = seriesNames.map((value) => _normalize(value));

        return dataInsights.filter((insight) => {
            const kpi = _normalize(insight.kpi);
            const serie = _normalize(insight.serie);

            return seriesNames.includes(kpi) || seriesNames.includes(serie);
        });
    }

    /**
     * @public
     * Verifica se visualização possui insights
     * @param {Object} param
     *
     * @returns {Array}
     */
    filterBySerie({
        companyId,
        date,
        period,
        source,
        scenarioId,
        seriesNames
    } = {}) {
        if (this._hasScenario(scenarioId)) return [];

        const selector = {
            companyId: { $eq: companyId },
            period: { $eq: period },
            date: { $eq: date },
            source: { $eq: source }
        };

        const options = {
            sort: { relevance: -1 },
            fields: {
                company: 0,
                companyId: 0,
                createdAt: 0,
                createdBy: 0,
                label: 0
            }
        };

        const { list } = this.findCollection(selector, options);

        const filtered = this._filter(list, seriesNames);

        return filtered;
    }

    /**
     * @public
     * Retorna insights no formato que dashbaords precisa
     * @param {Object} param
     * @returns {Array}
     */
    vizGet({ companyId, period, date, source, scenarioId } = {}) {
        const vizInsights = [];

        if (this._hasScenario(scenarioId)) return [];

        const selector = {
            companyId: { $eq: companyId },
            period: { $eq: period },
            date: { $eq: date }
        };

        if (source) selector.source = { $eq: source };

        const options = {
            fields: {
                _id: 1,
                kpi: 1,
                serie: 1,
                type: 1,
                source: 1
            }
        };

        const { list } = this.findCollection(selector, options);

        const total = list.length;

        if (!total) return [];

        for (let i = 0; i < total; i++) {
            const document = list[i];

            const name = document.serie;

            if (_.indexOf(vizInsights, name) == -1) {
                vizInsights.push({
                    name,
                    type: document.type
                });
            }
        }

        return vizInsights;
    }

    async generateInsightsByAccountingId(accountingId, source, options = {}) {
        if (!accountingId || !source) return null;
        const companiesWithDate = options.companiesWithDate;
        const fallbackDate = options.date;
        try {
            if (Array.isArray(companiesWithDate) && companiesWithDate.length) {
                await Promise.all(
                    companiesWithDate.map(({ companyId, date }) => {
                        if (
                            !companyId ||
                            date?.year == null ||
                            date?.month == null
                        ) {
                            return Promise.resolve();
                        }
                        return this.HttpAdapter.call(
                            `${this.baseUrlAnalyzer}/generate-by-companyid-source`,
                            {
                                accountingId,
                                companyId,
                                source,
                                date: {
                                    year: date.year,
                                    month: date.month
                                }
                            }
                        );
                    })
                );
                return true;
            }
            if (!fallbackDate?.year || fallbackDate?.month == null) return null;
            const user = new UserDataAdapter().user();
            const companyGateway = new Company();
            let companies = await companyGateway.getByAccountingId({
                accountingId,
                withContacts: false,
                summary: true,
                user,
                status: "ATIVO"
            });
            if (!companies?.length) return true;
            const companyIds = options.companyIds;
            if (Array.isArray(companyIds)) {
                if (!companyIds.length) return true;
                const companyIdSet = new Set(companyIds);
                companies = companies.filter((company) =>
                    companyIdSet.has(company.getId())
                );
                if (!companies.length) return true;
            }
            await Promise.all(
                companies.map((company) =>
                    this.HttpAdapter.call(
                        `${this.baseUrlAnalyzer}/generate-by-companyid-source`,
                        {
                            accountingId,
                            companyId: company.getId(),
                            source,
                            date: {
                                year: fallbackDate.year,
                                month: fallbackDate.month
                            }
                        }
                    )
                )
            );
            return true;
        } catch (error) {
            throw new Error(error.message);
        }
    }

    async insightsAnalyzer(payload) {
        const input = {
            accountingId: payload.company.accountingId,
            companyId: payload.company._id,
            source: payload.sources[0],
            date: {
                month: parseInt(payload.date.month),
                year: parseInt(payload.date.year)
            }
        };

        try {
            await this.HttpAdapter.call(
                `${this.baseUrlAnalyzer}/generate-by-companyid-source`,
                input
            );
            this.deleteMany({
                companyId: { $eq: input.companyId }
            });
            this.PopulateManager.deleteMany({
                collections: { $eq: "insights" }
            });
            await this.populateCompanyMonitoringBySource({
                accountingId: input.accountingId,
                companyId: input.companyId,
                source: input.source,
                type: input.type,
                date: input.date
            });

            return { companyId: payload.company._id };
        } catch (err) {
            return { companyId: payload.company._id };
        }
    }
}
